import _ from 'underscore';
import {
  shouldSelectBundleOption,
  parseLocationTargets,
  convertLocationStateToLocationSchemaCampaign,
} from '@bingads-webui-campaign/hotel-common-targets';
import { locationTargetsOptionKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import { CRITERION_LEVEL, bulkModelToViewModelForLocationTargetsBase } from './criterions';
import { getTargetCriterion } from './ad-group-criterions';
import { AddedDeviceBidAdjustment as DefaultDeviceBidAdjustment, RemovedDeviceBidAdjustment } from '../common/constants';
import { DeviceKeys } from '../steps/ad-group/target/device';

export const viewModelToBulkModelForDealTargets = model => ({
  CriterionType: 'DealCriterion',
  CampaignName: model.data.Name,
  Criterion: {
    Type: 'DealCriterion',
    DealCriterion: {
      DealId: _.get(model, ['data', 'deal', 'Id']),
    },
  },
});

export const modelToViewModelForLocationTargets = ({
  model, bundleCountries, campaignId, overrideOriginalBidAdjustment = false,
}) => {
  const {
    locations,
    intentId,
    intentOption,
  } = parseLocationTargets(model, 'campaign', campaignId);
  let targetOption = locations.length ? locationTargetsOptionKeys.specific : locationTargetsOptionKeys.everywhere;
  targetOption = shouldSelectBundleOption(bundleCountries, locations) ? locationTargetsOptionKeys.bundleOrDefaultMarket : targetOption;

  if (overrideOriginalBidAdjustment) {
    locations.forEach((location) => {
      // eslint-disable-next-line no-param-reassign
      location.Criterion.OriginalBidAdjustment = null;
    });
  }

  return {
    locationTargets: {
      currentLevelSettings: {
        initLocations: locations,
        currentLocations: locations,
        targetOption,
        intentId,
        ...intentOption,
      },
      bidMode: false,
      useParentLevel: false,
    },
  };
};

export const viewModelToBulkModelForLocationTargets = ({ bundleLocationCriterions, campaignViewModel }) => {
  if (!campaignViewModel.data.locationTargets || campaignViewModel.data.locationRestricted) {
    return [];
  }
  const bulkUpsertModel = convertLocationStateToLocationSchemaCampaign(
    campaignViewModel.data.locationTargets,
    bundleLocationCriterions,
    {
      bidStrategy: { option: 'placeholder' },
      ...campaignViewModel.data,
      name: {
        value: campaignViewModel.data.Name,
      },
    }
  );
  return bulkUpsertModel;
};

export const bulkModelToViewModelForLocationTargets = ({
  locationTargetsModel, bundleCountries,
}) => bulkModelToViewModelForLocationTargetsBase({
  locationTargetsModel, bundleCountries, level: CRITERION_LEVEL.CAMPAIGN,
});

export const viewModelToBulkModelForDeviceTargets = ({ campaignViewModel }) => {
  const deviceTargets = campaignViewModel.data.campaignLevelDeviceTargets || [];
  const initialDeviceTargets = campaignViewModel.data.initialCampaignLevelDeviceTargets || [];
  const campaignDeviceTypesWithBidAdjustment = campaignViewModel.data.campaignDeviceTypesWithBidAdjustment || [];
  const supportedDevices = [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones];

  const isBidAdjustmentSame = initialTarget => campaignDeviceTypesWithBidAdjustment.find(targetWithBid =>
    targetWithBid.Criterion.DeviceCriterion.DeviceName === initialTarget.target && targetWithBid.Criterion.BidAdjustment === initialTarget.bidAdjustment);
  const isTargetChanged = target => initialDeviceTargets.find(initialTarget => initialTarget.criterion === target && isBidAdjustmentSame(initialTarget)) === undefined;

  const addedDevices = deviceTargets.filter(isTargetChanged).map((target) => {
    const deviceTargetWithBid = campaignDeviceTypesWithBidAdjustment.find(deviceWithBid => deviceWithBid.Criterion.DeviceCriterion.DeviceName === target) || {};
    const initialDeviceTarget = initialDeviceTargets.find(initialTarget => initialTarget.criterion === target) || {};
    const { Id } = initialDeviceTarget || deviceTargetWithBid;
    const bid = _.get(deviceTargetWithBid, 'Criterion.BidAdjustment', initialDeviceTarget.bidAdjustment || DefaultDeviceBidAdjustment);

    return {
      Id,
      criterion: {
        Target: target,
        BidAdjustment: bid,
      },
    };
  });

  const toRemoveDevices = initialDeviceTargets.length === 0 && deviceTargets.length === 0 ? [] : _.difference(supportedDevices, deviceTargets); // don't remove devices if no change
  const isTargetInitiallyRemoved = target => initialDeviceTargets.find(initialTarget =>
    initialTarget.criterion === target && initialTarget.bidAdjustment === RemovedDeviceBidAdjustment);

  const removedDevices = toRemoveDevices.filter(target => !isTargetInitiallyRemoved(target)).map((target) => {
    const { Id } = initialDeviceTargets.find(initialTarget => initialTarget.criterion === target) || {};

    return {
      Id,
      criterion: {
        Target: target,
        BidAdjustment: RemovedDeviceBidAdjustment,
      },
    };
  });

  const defaultBidAdjustment = campaignDeviceTypesWithBidAdjustment ? undefined : DefaultDeviceBidAdjustment;

  const addedDeviceCriterion = getTargetCriterion({
    targets: addedDevices, type: 'Device', propKey: 'DeviceName', adGroupViewModel: null, campaignViewModel, bidAdjustment: defaultBidAdjustment, level: 'campaign',
  });

  const removedDeviceCriterion = getTargetCriterion({
    targets: removedDevices, type: 'Device', propKey: 'DeviceName', adGroupViewModel: null, campaignViewModel, bidAdjustment: RemovedDeviceBidAdjustment, level: 'campaign',
  });


  return [
    ...addedDeviceCriterion,
    ...removedDeviceCriterion,
  ];
};
